import { createBrowserRouter, RouterProvider } from 'react-router';
import NotFound from '@/app/routes/not-found.tsx';
import LoginPage from '@/app/routes/auth/login.tsx';
import { paths } from '@/config/paths.ts';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import type { ComponentType } from 'react';
import type { ActionFunction, LoaderFunction } from 'react-router';
import { useMemo } from 'react';
import { AdminRoute } from '@/lib/auth/admin-route';

type RouteModule = {
  default: ComponentType;
  clientLoader?: (queryClient: QueryClient) => LoaderFunction;
  clientAction?: (queryClient: QueryClient) => ActionFunction;
} & Record<string, unknown>;

const convert = (queryClient: QueryClient) => (m: RouteModule) => {
  const { clientLoader, clientAction, default: Component, ...rest } = m;
  return {
    ...rest,
    loader: clientLoader?.(queryClient),
    action: clientAction?.(queryClient),
    Component,
  };
};

const createAppRouter = (queryClient: QueryClient) =>
  createBrowserRouter([
    {
      path: '/',
      element: <LoginPage />,
    },
    {
      path: paths.app.root.path,
      children: [
        {
          path: paths.app.dashboard.path,
          lazy: () =>
            import('@/app/routes/dashboard.tsx').then(convert(queryClient)),
        },
        {
          path: paths.app.profile.path,
          lazy: () =>
            import('@/app/routes/profile.tsx').then(convert(queryClient)),
        },
        // Practices routes
        {
          path: paths.app.practices.list.path,
          lazy: () =>
            import('@/app/routes/practices/list.tsx').then(convert(queryClient)),
        },
        {
          path: paths.app.practices.detail.path,
          lazy: () =>
            import('@/app/routes/practices/detail.tsx').then(convert(queryClient)),
        },
        {
          path: paths.app.practices.edit.path,
          lazy: () =>
            import('@/app/routes/practices/edit.tsx').then(convert(queryClient)),
        },
        // Requests routes
        {
          path: paths.app.requests.list.path,
          lazy: () =>
            import('@/app/routes/requests/list.tsx').then(convert(queryClient)),
        },
        {
          path: paths.app.requests.detail.path,
          lazy: () =>
            import('@/app/routes/requests/detail.tsx').then(
              convert(queryClient),
            ),
        },
        {
          path: paths.app.requests.createProposals.path,
          lazy: () =>
            import('@/app/routes/requests/create-proposals.tsx').then(
              convert(queryClient),
            ),
        },
        // Catalog routes (Admin only)
        {
          path: paths.app.catalog.operators.path,
          lazy: async () => {
            const module = await import('@/app/routes/catalog/operators.tsx');
            return {
              Component: () => (
                <AdminRoute>
                  <module.default />
                </AdminRoute>
              ),
            };
          },
        },
        {
          path: paths.app.catalog.pricingConstants.path,
          lazy: async () => {
            const module =
              await import('@/app/routes/catalog/pricing-constants.tsx');
            return {
              Component: () => (
                <AdminRoute>
                  <module.default />
                </AdminRoute>
              ),
            };
          },
        },
        // Offers routes
        {
          path: paths.app.catalog.offers.list.path,
          lazy: async () => {
            const module = await import('@/app/routes/catalog/offers.tsx');
            return {
              Component: () => (
                <AdminRoute>
                  <module.default />
                </AdminRoute>
              ),
            };
          },
        },
        {
          path: paths.app.catalog.offers.new.path,
          lazy: async () => {
            const module = await import('@/app/routes/catalog/offers-new.tsx');
            return {
              Component: () => (
                <AdminRoute>
                  <module.default />
                </AdminRoute>
              ),
            };
          },
        },
        {
          path: paths.app.catalog.offers.detail.path,
          lazy: async () => {
            const module = await import('@/app/routes/catalog/offer-edit.tsx');
            return {
              Component: () => (
                <AdminRoute>
                  <module.default />
                </AdminRoute>
              ),
            };
          },
        },
      ],
    },
    {
      path: '*',
      element: <NotFound />,
    },
  ]);

export const AppRoutes = () => {
  const queryClient = useQueryClient();

  const router = useMemo(() => createAppRouter(queryClient), [queryClient]);

  return <RouterProvider router={router} />;
};
